import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import { embeddingService } from "@/services/embeddingService";
import { embeddingJobService } from "@/services/embeddingJobService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import type {
  IntegrationContentMode,
  IntegrationIndexItem,
  IntegrationIndexRecord,
  IntegrationIndexResult,
  IntegrationSearchResult,
  IntegrationSource,
} from "@/lib/integrationRag/types";
import {
  formatIntegrationDisplayTitle,
  integrationTargetId,
} from "@/lib/integrationRag/types";

export type IndexItemsOptions = {
  contentMode?: IntegrationContentMode;
  onProgress?: (label: string, completed: number, total: number) => void;
};

class IntegrationRagService {
  private async requireUserId(): Promise<string> {
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");
    return userId;
  }

  async getIndexRecords(source?: IntegrationSource): Promise<IntegrationIndexRecord[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await this.requireUserId();

    let query = supabase
      .from("integration_index")
      .select("source, external_id, title, indexed_at, remote_updated_at, chunk_count, metadata")
      .eq("user_id", userId);

    if (source) query = query.eq("source", source);

    const { data, error } = await query.order("indexed_at", { ascending: false });
    if (error) {
      console.error("[integrationRag] getIndexRecords:", error);
      return [];
    }

    return (data ?? []) as IntegrationIndexRecord[];
  }

  async indexItems(
    items: IntegrationIndexItem[],
    options: IndexItemsOptions = {}
  ): Promise<IntegrationIndexResult> {
    const contentMode = options.contentMode ?? "raw";
    const result: IntegrationIndexResult = { indexed: [], failed: [] };

    if (items.length === 0) return result;

    const jobs = items
      .filter((item) => item.content.trim())
      .map((item) => ({
        job_type: "integration" as const,
        target_id: integrationTargetId(item.source, item.externalId),
        payload: {
          source: item.source,
          externalId: item.externalId,
          title: item.title,
          content: item.content,
          contentMode,
          metadata: item.metadata ?? {},
          remoteUpdatedAt: item.remoteUpdatedAt ?? null,
          forceReindex: true,
        },
      }));

    const skipped = items.filter((item) => !item.content.trim());
    for (const item of skipped) {
      result.failed.push({ externalId: item.externalId, error: "No content to index" });
    }

    if (jobs.length === 0) return result;

    // Outcome per job, keyed by target id. Previously every item was recorded
    // as indexed once the wait returned, whatever the jobs actually did — so a
    // failed or never-queued job still showed as "indexed" in the UI.
    const outcome = new Map<string, { status: string; error?: string | null }>();
    let completed = 0;
    let waitError: string | null = null;

    try {
      await embeddingJobService.enqueueAndWait(jobs, (job) => {
        const p = job.progress ?? {};
        const title = typeof p.title === "string" ? p.title : job.target_id;
        outcome.set(job.target_id, { status: job.status, error: job.last_error });
        options.onProgress?.(title, completed, jobs.length);
        if (job.status === "completed") completed += 1;
      });
    } catch (err) {
      // enqueueAndWait throws on a failed job, a timeout, or nothing queued.
      // Whatever it managed to report is still in `outcome`; the rest are
      // attributed below using this message.
      waitError = err instanceof Error ? err.message : "Embedding failed";
    }

    for (const item of items.filter((i) => i.content.trim())) {
      const targetId = integrationTargetId(item.source, item.externalId);
      const job = outcome.get(targetId);
      if (job?.status === "completed") {
        result.indexed.push(item.externalId);
      } else {
        result.failed.push({
          externalId: item.externalId,
          error:
            job?.error ||
            waitError ||
            (job ? `Indexing ${job.status}` : "Indexing did not complete"),
        });
      }
    }

    const indexedIds = new Set(result.indexed);
    const userId = await this.requireUserId();
    void import("@/services/operatingLoopService").then(({ scheduleOperatingBrief }) => {
      for (const item of items.filter((i) => indexedIds.has(i.externalId))) {
        scheduleOperatingBrief({
          userId,
          documentId: integrationTargetId(item.source, item.externalId),
          title: item.title,
          content: item.content,
        });
      }
    }).catch((err) => {
      // Briefs are a background nicety; a failure here must not surface as an
      // unhandled rejection or affect the indexing result the caller sees.
      console.warn("[integrationRag] scheduling operating briefs failed:", err);
    });

    return result;
  }

  async search(
    query: string,
    options: {
      sources?: IntegrationSource[];
      limit?: number;
      similarityThreshold?: number;
    } = {}
  ): Promise<IntegrationSearchResult[]> {
    if (!query.trim() || !isSupabaseConfigured) return [];

    const userId = await this.requireUserId();
    const { limit = 10, similarityThreshold = 0.5, sources } = options;

    const { embedding } = await embeddingService.generateEmbedding(query);

    const { data, error } = await supabase.rpc("match_integration_chunks_v2", {
      query_embedding: JSON.stringify(embedding),
      filter_user_id: userId,
      filter_sources: sources?.length ? sources : null,
      match_threshold: similarityThreshold,
      match_count: limit,
    });

    if (error) {
      console.error("[integrationRag] search:", error);
      return [];
    }

    return (data ?? []).map((row: Record<string, unknown>) => {
      const meta = (row.metadata as Record<string, unknown>) ?? {};
      const contentMode = meta.contentMode === "agent" ? "agent" : "raw";
      const source = row.source as IntegrationSource;
      const title = String(row.title ?? row.external_id ?? "Indexed item");
      return {
        source,
        externalId: String(row.external_id),
        title: formatIntegrationDisplayTitle(source, title),
        chunkText: String(row.chunk_text ?? ""),
        chunkIndex: Number(row.chunk_index ?? 0),
        similarity: Number(row.similarity ?? 0),
        metadata: meta,
        contentMode,
      };
    });
  }
}

export const integrationRagService = new IntegrationRagService();

export async function saveIntegrationLibraryBackup(input: {
  source: IntegrationSource;
  externalId: string;
  title: string;
  content: string;
  metadata?: Record<string, unknown>;
  workspaceId?: string | null;
}): Promise<string> {
  const backupKey = `${input.source}:${input.externalId}`;
  const existing = await knowledgeBaseService.getKnowledgeBaseItems();
  const match = existing.find((item) => {
    const meta = item.metadata as Record<string, unknown> | undefined;
    return meta?.integrationBackupKey === backupKey;
  });

  const metadata = {
    ...(input.metadata ?? {}),
    source: input.source,
    integrationBackupKey: backupKey,
    integrationExternalId: input.externalId,
  };

  if (match) {
    await knowledgeBaseService.updateKnowledgeBaseItem(match.id, {
      title: input.title,
      content: input.content,
      metadata,
      includeInRag: false,
    });
    return match.id;
  }

  const item = await knowledgeBaseService.createKnowledgeBaseItem(
    {
      title: input.title,
      content: input.content,
      type: "note",
      includeInRag: false,
      metadata,
    },
    input.workspaceId ?? null
  );
  return item.id;
}
